import { httpsCallable } from 'firebase/functions'

import { functions } from '../firebase'
import type { UserTokenStatus, RateLimitError } from '../types/token.types'

/**
 * Frontend service for interacting with backend token system
 */
export class TokenService {
  /**
   * Get current token status for the authenticated user
   * @returns Promise<UserTokenStatus> - Current token status
   */
  static async getUserTokenStatus(): Promise<UserTokenStatus> {
    try {
      const getMyTokenStatusFunction = httpsCallable<
        void,
        {
          success: boolean
          data?: UserTokenStatus
          error?: string
        }
      >(functions, 'getMyTokenStatus')

      const result = await getMyTokenStatusFunction()

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to get token status')
      }

      if (!result.data.data) {
        throw new Error('No token status data returned')
      }

      // Convert refillsAt string back to Date object
      const tokenStatus = {
        ...result.data.data,
        refillsAt: new Date(result.data.data.refillsAt),
      }

      return tokenStatus
    } catch (error) {
      console.error('Error getting token status:', error)

      // Handle specific Firebase errors
      if (error instanceof Error) {
        if (error.message.includes('unauthenticated')) {
          throw new Error('Please sign in to view token status')
        }
        if (error.message.includes('permission-denied')) {
          throw new Error(
            'You do not have permission to access token information'
          )
        }
      }

      throw new Error(
        `Failed to get token status: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  /**
   * Check if a rate limit error is a token-related error
   * @param error - Error to check
   * @returns boolean - True if it's a rate limit error
   */
  static isRateLimitError(error: unknown): error is RateLimitError {
    return (
      error instanceof Error &&
      'code' in error &&
      (error as { code: string }).code === 'functions/resource-exhausted'
    )
  }

  /**
   * Extract token information from a Firebase Functions error
   * @param error - Firebase Functions error
   * @returns RateLimitError with token info
   */
  static parseRateLimitError(error: {
    message?: string
    details?: {
      tokensRemaining?: number
      maxTokens?: number
      refillsAt?: string
    }
  }): RateLimitError {
    const tokenInfo = error.details || {}

    return {
      name: 'RateLimitError',
      message: error.message || 'Daily request limit exceeded',
      code: 'RATE_LIMIT_EXCEEDED',
      tokenInfo: {
        tokensRemaining: tokenInfo.tokensRemaining || 0,
        maxTokens: tokenInfo.maxTokens || 10,
        refillsAt: tokenInfo.refillsAt
          ? new Date(tokenInfo.refillsAt)
          : new Date(),
      },
    } as RateLimitError
  }

  /**
   * Format time until next refill in human-readable format
   * @param refillTime - Date when tokens refill
   * @returns string - Formatted time (e.g., "in 4 hours", "tomorrow at midnight")
   */
  static formatTimeUntilRefill(refillTime: Date): string {
    const now = new Date()
    const diffMs = refillTime.getTime() - now.getTime()

    if (diffMs <= 0) {
      return 'now'
    }

    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffDays > 0) {
      if (diffDays === 1) {
        return 'tomorrow'
      }
      return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`
    }

    if (diffHours === 1) {
      return 'in 1 hour'
    }

    return `in ${diffHours} hours`
  }

  /**
   * Calculate token usage percentage
   * @param current - Current tokens
   * @param max - Maximum tokens
   * @returns number - Percentage (0-100)
   */
  static calculateTokenPercentage(current: number, max: number): number {
    if (max <= 0) return 0
    return Math.round((current / max) * 100)
  }

  /**
   * Determine if token count is in a warning state
   * @param current - Current tokens
   * @param max - Maximum tokens
   * @returns object - Warning state information
   */
  static getTokenWarningState(
    current: number,
    max: number
  ): {
    isLow: boolean
    isCritical: boolean
    isEmpty: boolean
  } {
    const percentage = this.calculateTokenPercentage(current, max)

    return {
      isEmpty: current === 0,
      isCritical: percentage <= 10 && current > 0, // 1 token or less for 10-token users
      isLow: percentage <= 30 && current > 0, // 3 tokens or less for 10-token users
    }
  }

  /**
   * Get next refill time (midnight UTC)
   * @returns Date - Next midnight UTC
   */
  static getNextRefillTime(): Date {
    const now = new Date()
    const tomorrow = new Date(now)
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1)
    tomorrow.setUTCHours(0, 0, 0, 0)
    return tomorrow
  }
}
